export interface Progress {
  packagesCount: number;
  overallProgress: number;
}

export interface Avatar {
  url?: string;
  alt?: string;
}

import { Package } from "./packages.interfaces";

/** Aligns with API JSON (camelCase strings). */
export enum ModuleTestProgress {
  NotStarted = "notStarted",
  InProgress = "inProgress",
  Completed = "completed",
}

export interface StudentPackage extends Package {
  purchaseDate: string;
  overallProgress: number;
  consecutiveDays: number;
  inactiveDays: number;
}

/** Module item in the package management view (student progress per module). */
export interface StudentPackageModule {
  moduleId: string;
  title: string;
  /** Internal/slug name of the module. */
  internalName?: string | null;
  order: number;
  progressPercentage: number | null;
  exercisesAnswered: number;
  totalExercises: number;
  lastActivityAt: string | null;
  sessionStatus?: "Active" | "Completed" | "Abandoned";
  /** Whether the module is unlocked for the student (admin can toggle). */
  isUnlocked: boolean;
  /** Module test lifecycle for this module (not started / in progress / completed). */
  moduleTestProgress: ModuleTestProgress;
}

export interface StudentLevelTest {
  hasSession: boolean;
  finalLevel?: number;
  finalLevelName?: string;
  status?: string;
}

export interface Student {
  id: string;
  name: string;
  email: string;
  status: "Active" | "Inactive";
  lastLogin: string;
  enrollmentDate: string;
  progress: Progress;
  avatar?: Avatar;
  isLocked?: boolean;
  lastPasswordReset?: string;
  packages: StudentPackage[];
  notes?: string; // Added notes property here
  levelTest?: StudentLevelTest;
  /** Identity user id; used e.g. for resending confirmation email. */
  userId?: string | null;
  /** Whether the user has confirmed their email. When false, admin can resend activation email. */
  emailConfirmed?: boolean;
}

export interface CreateStudentInput {
  name: string;
  email: string;
  status: "Active" | "Inactive";
  enrollmentDate: string;
  progress: Progress;
  avatar?: Avatar;
}

export interface UpdateStudentInput {
  name?: string;
  email?: string;
  status?: "Active" | "Inactive";
  enrollmentDate?: string;
  progress?: Progress;
  avatar?: Avatar;
  isLocked?: boolean;
  lastPasswordReset?: string;
}

/** Admin GET module test session (api/admin/students/.../module-test-session). */
export interface ModuleTestExerciseSummary {
  id: string;
  title?: string | null;
  type: string;
  data: string;
}

export interface ModuleTestAnswerWithExercise {
  exerciseId: string;
  answerData: string;
  isCorrect: boolean;
  answeredAt: string;
  exercise?: ModuleTestExerciseSummary | null;
}

export interface ModuleUnlockedAfterPass {
  moduleId: string;
  moduleTitle?: string | null;
  packageId: string;
  packageName?: string | null;
}

export interface ModuleTestSessionAdmin {
  id: string;
  studentId: string;
  moduleId: string;
  createdAt: string;
  finished: boolean;
  scorePercent: number | null;
  passed: boolean | null;
  completedAt?: string | null;
  answers: ModuleTestAnswerWithExercise[];
  /** When passed: next module(s) per package, aligned with server unlock on finish. */
  unlockedAfterPass?: ModuleUnlockedAfterPass[] | null;
}
